import { assertNever } from './exhaustive';
import {
  CURRENCY_CODE_PATTERN,
  type FieldType,
  type FieldTypeOptions,
  type FilterFamily,
  type FilterOperator,
  filterFamilyOf,
  isNullaryOperator,
  supportsOperator,
} from './field-types';
import { SELECT_OPTION_ID_PATTERN } from './ids';

/**
 * INV-6 ONE FILTER SEMANTICS, first half: ONE operand parser.
 *
 * Both backends consume the NORMALIZED operand, never the raw JSON the client
 * sent. That is the structural reason they cannot drift: "is `1500000` micros or
 * euros", "does `contains` take one tag or a set", "is this a legal option id" are
 * answered exactly once, here, and a divergence would have to be a divergence in
 * this file rather than a mismatch between two independent readings of a payload.
 *
 * Everything this module rejects, it rejects LOUDLY. A filter with an operand the
 * backend cannot use must fail the request, not silently match nothing: a saved
 * view that quietly returns zero rows is indistinguishable from a real empty result.
 */

export type Operand =
  | { readonly kind: 'none' }
  | { readonly kind: 'text'; readonly value: string }
  | { readonly kind: 'textList'; readonly values: readonly string[] }
  | { readonly kind: 'number'; readonly value: number }
  | { readonly kind: 'numberRange'; readonly lo: number; readonly hi: number }
  | { readonly kind: 'boolean'; readonly value: boolean }
  | { readonly kind: 'date'; readonly value: string }
  | { readonly kind: 'dateRange'; readonly lo: string; readonly hi: string }
  | { readonly kind: 'datetime'; readonly value: string }
  | { readonly kind: 'datetimeRange'; readonly lo: string; readonly hi: string }
  | { readonly kind: 'option'; readonly value: string }
  | { readonly kind: 'optionList'; readonly values: readonly string[] }
  | { readonly kind: 'id'; readonly value: string }
  | { readonly kind: 'idList'; readonly values: readonly string[] };

export interface NormalizedCondition {
  readonly fieldType: FieldType;
  readonly family: FilterFamily;
  readonly operator: FilterOperator;
  readonly operand: Operand;
  /**
   * CURRENCY only, and optional: when the caller pinned a currency the condition
   * gains an AND equality on the code sub-column, so "over 1000" cannot silently
   * mean "over 1000 of whatever currency happens to be in the row".
   */
  readonly currencyCode?: string | undefined;
}

export class FilterOperandError extends Error {
  override readonly name = 'FilterOperandError';
  constructor(
    readonly fieldType: FieldType,
    readonly operator: FilterOperator,
    message: string,
  ) {
    super(`filter ${fieldType}.${operator}: ${message}`);
  }
}

/** What ONE operator expects, given the field type it is applied to. */
export type OperandShape = 'none' | 'single' | 'list' | 'range';

/**
 * MULTI_SELECT is the reason this depends on the field type as well as the
 * operator: `contains` takes one string for TEXT and a SET of option ids for a
 * MULTI_SELECT. Everything else is decided by the operator alone.
 */
export function operandShape(fieldType: FieldType, operator: FilterOperator): OperandShape {
  if (isNullaryOperator(operator)) return 'none';
  if (fieldType === 'MULTI_SELECT') return 'list';
  if (operator === 'in' || operator === 'notIn') return 'list';
  if (operator === 'between') return 'range';
  return 'single';
}

/**
 * Shape AND calendar. `2026-02-30` passes the shape and is a real trap: PostgreSQL refuses
 * `'2026-02-30'::date` with a 22008, which surfaced as a flat 500 instead of the 400 this module
 * promises, WHILE the JS evaluator happily compared it lexically - an INV-6 divergence the corpus
 * could not see, because every fixture in it is a date that exists. The registry's own
 * `z.iso.date()` rejects it, so this is a `Date` round trip rather than a second calendar.
 */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function isRealCalendarDate(value: string): boolean {
  const parsed = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return false;
  // Round-tripped, not just parsed: an engine that NORMALISES would roll 2026-02-30 to March 2nd
  // and silently accept it.
  return parsed.toISOString().slice(0, 10) === value;
}
const ISO_DATETIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,9})?(Z|[+-]\d{2}:\d{2})$/;

function fail(fieldType: FieldType, operator: FilterOperator, message: string): never {
  throw new FilterOperandError(fieldType, operator, message);
}

/**
 * How many elements an `in` / `contains` set may carry.
 *
 * Bounded because each element becomes its own bind parameter (see the SQL backend's
 * `valueList`): a list long enough to exhaust PostgreSQL's parameter budget would fail
 * as a driver error deep in a query rather than as a readable rejection here.
 */
export const MAX_OPERAND_LIST_LENGTH = 1000;

/** A raw operand as a list: a bare scalar counts as a one-element set. */
function asList(fieldType: FieldType, operator: FilterOperator, raw: unknown): readonly unknown[] {
  const list = Array.isArray(raw) ? raw : [raw];
  if (list.length > MAX_OPERAND_LIST_LENGTH) {
    fail(fieldType, operator, `a set operand may hold at most ${MAX_OPERAND_LIST_LENGTH} values`);
  }
  return list;
}

function asPair(
  fieldType: FieldType,
  operator: FilterOperator,
  raw: unknown,
): readonly [unknown, unknown] {
  if (!Array.isArray(raw) || raw.length !== 2) {
    fail(fieldType, operator, 'expected a two-element [from, to] array');
  }
  return [raw[0], raw[1]];
}

function requireString(fieldType: FieldType, operator: FilterOperator, raw: unknown): string {
  if (typeof raw !== 'string') fail(fieldType, operator, `expected a string, got ${typeOf(raw)}`);
  return raw;
}

function requireNumber(fieldType: FieldType, operator: FilterOperator, raw: unknown): number {
  if (typeof raw !== 'number' || !Number.isFinite(raw)) {
    fail(fieldType, operator, `expected a finite number, got ${typeOf(raw)}`);
  }
  return raw;
}

function requireDate(fieldType: FieldType, operator: FilterOperator, raw: unknown): string {
  const value = requireString(fieldType, operator, raw);
  if (!ISO_DATE.test(value))
    fail(fieldType, operator, `expected a YYYY-MM-DD date, got "${value}"`);
  if (!isRealCalendarDate(value)) {
    fail(fieldType, operator, `"${value}" is not a date that exists in the calendar`);
  }
  return value;
}

function requireDatetime(fieldType: FieldType, operator: FilterOperator, raw: unknown): string {
  const value = requireString(fieldType, operator, raw);
  if (!ISO_DATETIME.test(value) || Number.isNaN(Date.parse(value))) {
    fail(fieldType, operator, `expected an ISO-8601 timestamp with an offset, got "${value}"`);
  }
  // The same calendar trap as DATE, checked explicitly so the two temporal families cannot drift.
  if (!isRealCalendarDate(value.slice(0, 10))) {
    fail(fieldType, operator, `"${value}" is not a date that exists in the calendar`);
  }
  return value;
}

function requireOptionId(
  fieldType: FieldType,
  operator: FilterOperator,
  raw: unknown,
  options: FieldTypeOptions | undefined,
): string {
  const value = requireString(fieldType, operator, raw);
  if (!SELECT_OPTION_ID_PATTERN.test(value)) {
    fail(
      fieldType,
      operator,
      `"${value}" is not an option id. Filters compare option IDS, never labels - a label is display text and renaming it must not break a saved view`,
    );
  }
  const allowed = options?.selectOptions;
  // Only when the field's options are known: the same normalizer runs on a client
  // that may not have the metadata yet, and inventing a rejection there would be
  // worse than letting the server have the last word.
  if (allowed !== undefined && !allowed.some((option) => option.id === value)) {
    fail(
      fieldType,
      operator,
      `option ${value} does not exist on this field (has: ${allowed.map((option) => option.id).join(', ') || 'none'})`,
    );
  }
  return value;
}

const UUID = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/**
 * Lowercased, because PostgreSQL renders a uuid as lowercase and one of the compiled
 * predicates compares `uuid::text`. An operand of `018F...` would then match nothing
 * while `018f...` matched - a difference no caller could be expected to know about, and
 * one the JS evaluator (which compares the stored lowercase string) would share.
 */
function requireUuid(fieldType: FieldType, operator: FilterOperator, raw: unknown): string {
  const value = requireString(fieldType, operator, raw);
  if (!UUID.test(value)) fail(fieldType, operator, `expected a UUID, got "${value}"`);
  return value.toLowerCase();
}

function typeOf(raw: unknown): string {
  if (raw === null) return 'null';
  if (Array.isArray(raw)) return 'an array';
  return `a ${typeof raw}`;
}

/**
 * CURRENCY accepts the amount alone or the amount with a currency, in MICROS.
 *
 * A bare number is allowed here (unlike on the write path) because a filter cannot
 * be ambiguous the way a stored value can: the field's operators are numeric and
 * the column is `amount_micros`, so there is exactly one thing the number can mean.
 * The unit is still spelled out in the error messages, because "1000" meaning a
 * tenth of a cent surprises everybody exactly once.
 */
function splitCurrency(raw: unknown): { readonly amount: unknown; readonly code?: string } {
  if (typeof raw === 'object' && raw !== null && !Array.isArray(raw)) {
    const input = raw as { amountMicros?: unknown; currencyCode?: unknown };
    if (input.amountMicros !== undefined) {
      return typeof input.currencyCode === 'string'
        ? { amount: input.amountMicros, code: input.currencyCode }
        : { amount: input.amountMicros };
    }
  }
  return { amount: raw };
}

function normalizeText(
  fieldType: FieldType,
  operator: FilterOperator,
  shape: OperandShape,
  raw: unknown,
): Operand {
  if (shape === 'list') {
    return {
      kind: 'textList',
      values: asList(fieldType, operator, raw).map((item) =>
        requireString(fieldType, operator, item),
      ),
    };
  }
  if (shape === 'range') fail(fieldType, operator, 'text fields have no range operators');
  return { kind: 'text', value: requireString(fieldType, operator, raw) };
}

function normalizeNumber(
  fieldType: FieldType,
  operator: FilterOperator,
  shape: OperandShape,
  raw: unknown,
): Operand {
  if (shape === 'range') {
    const [lo, hi] = asPair(fieldType, operator, raw);
    return {
      kind: 'numberRange',
      lo: requireNumber(fieldType, operator, lo),
      hi: requireNumber(fieldType, operator, hi),
    };
  }
  if (shape === 'list') fail(fieldType, operator, 'number fields have no set operators');
  return { kind: 'number', value: requireNumber(fieldType, operator, raw) };
}

function normalizeDate(
  fieldType: FieldType,
  operator: FilterOperator,
  shape: OperandShape,
  raw: unknown,
): Operand {
  if (shape === 'range') {
    const [lo, hi] = asPair(fieldType, operator, raw);
    return {
      kind: 'dateRange',
      lo: requireDate(fieldType, operator, lo),
      hi: requireDate(fieldType, operator, hi),
    };
  }
  if (shape === 'list') fail(fieldType, operator, 'date fields have no set operators');
  return { kind: 'date', value: requireDate(fieldType, operator, raw) };
}

function normalizeDatetime(
  fieldType: FieldType,
  operator: FilterOperator,
  shape: OperandShape,
  raw: unknown,
): Operand {
  if (shape === 'range') {
    const [lo, hi] = asPair(fieldType, operator, raw);
    return {
      kind: 'datetimeRange',
      lo: requireDatetime(fieldType, operator, lo),
      hi: requireDatetime(fieldType, operator, hi),
    };
  }
  if (shape === 'list') fail(fieldType, operator, 'datetime fields have no set operators');
  return { kind: 'datetime', value: requireDatetime(fieldType, operator, raw) };
}

function normalizeOption(
  fieldType: FieldType,
  operator: FilterOperator,
  shape: OperandShape,
  raw: unknown,
  options: FieldTypeOptions | undefined,
): Operand {
  if (shape === 'list') {
    return {
      kind: 'optionList',
      values: asList(fieldType, operator, raw).map((item) =>
        requireOptionId(fieldType, operator, item, options),
      ),
    };
  }
  if (shape === 'range') fail(fieldType, operator, 'select fields have no range operators');
  return { kind: 'option', value: requireOptionId(fieldType, operator, raw, options) };
}

function normalizeId(
  fieldType: FieldType,
  operator: FilterOperator,
  shape: OperandShape,
  raw: unknown,
): Operand {
  if (shape === 'list') {
    return {
      kind: 'idList',
      values: asList(fieldType, operator, raw).map((item) =>
        requireUuid(fieldType, operator, item),
      ),
    };
  }
  if (shape === 'range') fail(fieldType, operator, 'relation fields have no range operators');
  return { kind: 'id', value: requireUuid(fieldType, operator, raw) };
}

/**
 * Raw filter value -> the typed operand both backends execute.
 *
 * `options` is the field's own configuration from metadata. It is optional so the
 * browser can normalize before its metadata query resolves; when present, option
 * ids are checked against the field's actual list.
 */
export function normalizeCondition(
  fieldType: FieldType,
  operator: FilterOperator,
  raw: unknown,
  options?: FieldTypeOptions,
): NormalizedCondition {
  if (!supportsOperator(fieldType, operator)) {
    fail(fieldType, operator, 'this operator is not declared for this field type');
  }

  const family = filterFamilyOf(fieldType);
  const shape = operandShape(fieldType, operator);
  if (shape === 'none') {
    if (raw !== undefined) fail(fieldType, operator, 'this operator takes no value');
    return { fieldType, family, operator, operand: { kind: 'none' } };
  }
  if (raw === undefined) fail(fieldType, operator, 'this operator requires a value');

  switch (family) {
    case 'text':
      return {
        fieldType,
        family,
        operator,
        operand: normalizeText(fieldType, operator, shape, raw),
      };
    case 'number':
      return {
        fieldType,
        family,
        operator,
        operand: normalizeNumber(fieldType, operator, shape, raw),
      };
    case 'currency': {
      const { amount, code } = splitCurrency(raw);
      if (code !== undefined && !CURRENCY_CODE_PATTERN.test(code)) {
        fail(fieldType, operator, `"${code}" is not an ISO-4217 currency code`);
      }
      const operand = normalizeNumber(fieldType, operator, shape, amount);
      // Micros are stored as bigint, so a fractional operand has no representation:
      // refuse it rather than let the driver fail on `1.5` deep inside a query.
      const amounts =
        operand.kind === 'numberRange'
          ? [operand.lo, operand.hi]
          : [(operand as { value: number }).value];
      for (const value of amounts) {
        if (!Number.isInteger(value)) {
          fail(
            fieldType,
            operator,
            `amounts are integers in micros (1 unit = 1000000); got ${value}`,
          );
        }
      }
      return code === undefined
        ? { fieldType, family, operator, operand }
        : { fieldType, family, operator, operand, currencyCode: code };
    }
    case 'boolean': {
      if (typeof raw !== 'boolean') {
        fail(fieldType, operator, `expected true or false, got ${typeOf(raw)}`);
      }
      return { fieldType, family, operator, operand: { kind: 'boolean', value: raw } };
    }
    case 'date':
      return {
        fieldType,
        family,
        operator,
        operand: normalizeDate(fieldType, operator, shape, raw),
      };
    case 'datetime':
      return {
        fieldType,
        family,
        operator,
        operand: normalizeDatetime(fieldType, operator, shape, raw),
      };
    case 'option':
      return {
        fieldType,
        family,
        operator,
        operand: normalizeOption(fieldType, operator, shape, raw, options),
      };
    case 'id':
      return { fieldType, family, operator, operand: normalizeId(fieldType, operator, shape, raw) };
    /**
     * THE NINTH FAMILY'S ONE ARM (`file-attachments.md` D10). It is unreachable through the
     * product - `isEmpty` and `isNotEmpty` are nullary and this function has already
     * returned for them - and it is written out rather than folded into a neighbouring
     * family so that a caller who hand-builds a FILES condition with an operand is told
     * what a FILES field actually supports.
     */
    case 'files':
      return fail(
        fieldType,
        operator,
        'a FILES field has no operand: it supports isEmpty and isNotEmpty only',
      );
    default:
      return assertNever(family, 'normalizeCondition');
  }
}
